import React from 'react';
import HwzComponent from '../../../common/component/HwzComponent';
import { Button, Modal, Row, Col, Input, Space, Tag, List, Avatar } from 'antd';
import { CopyOutlined, ExclamationCircleFilled, UserOutlined, TeamOutlined } from '@ant-design/icons';
import './ShareOperationModal.less';
import Share from '../../../common/model/share/Share';
import Lang from '../../../common/model/global/Lang';
import ClipboardUtil from '../../../common/util/ClipboardUtil';
import MessageBoxUtil from '../../../common/util/MessageBoxUtil';
import DateUtil from '../../../common/util/DateUtil';

interface IProps {
  share: Share;
  onClose: () => any;
}

interface IState {}

export default class ShareOperationModal extends HwzComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);
  }

  static open = (share: Share) => {
    const modal = Modal.confirm({
      className: 'share-link-modal custom-handle-modal',
      title: share.isPrivateShare() ? Lang.t('share.privateShareDetail') : Lang.t('share.shareLinkAndCode'),
      icon: <ExclamationCircleFilled twoToneColor="#FFDC00" />,
      width: '600px',
      okCancel: false,
      content: (
        <ShareOperationModal
          share={share}
          onClose={() => {
            modal.destroy();
          }}
        />
      ),
    });
  };

  // 将逗号分隔的字符串转换为数组
// 替代的安全转换方法
getTargetUserUuids(): string[] {
  const { share } = this.props;
  try {
    if (!share.targetUserUuids) return [];
    
    // 如果是数组，直接返回
    if (Array.isArray(share.targetUserUuids)) {
      return share.targetUserUuids;
    }
    
    // 如果是字符串，尝试分割
    const targetUserUuids = String(share.targetUserUuids);
    if (targetUserUuids) {
      return targetUserUuids.split(',').filter(uuid => uuid.trim() !== '');
    }
    
    return [];
  } catch (error) {
    console.error('Error parsing targetUserUuids:', error);
    return [];
  }
}

  // 将逗号分隔的字符串转换为数组
  getTargetSpaceUuids(): string[] {
    const { share } = this.props;
    try {
      if (!share.targetSpaceUuids) return [];
      
      // 如果是数组，直接返回
      if (Array.isArray(share.targetSpaceUuids)) {
        return share.targetSpaceUuids;
      }
      
      // 如果是字符串，尝试分割
      const targetSpaceUuids = String(share.targetSpaceUuids);
      if (targetSpaceUuids) {
        return targetSpaceUuids.split(',').filter(uuid => uuid.trim() !== '');
      }
      
      return [];
    } catch (error) {
      console.error('Error parsing targetSpaceUuids:', error);
      return [];
    }
  }

  copyLink = () => {
    const { share } = this.props;
    const url = share.getLink();
    ClipboardUtil.copy(url, () => {
      MessageBoxUtil.success(Lang.t('share.copyLinkSuccess'));
    });
  };

  copyCode = () => {
    const { share } = this.props;
    if (share.code) {
      ClipboardUtil.copy(share.code, () => {
        MessageBoxUtil.success(Lang.t('share.copyCodeSuccess'));
      });
    }
  };

  copyBoth = () => {
    const { share } = this.props;
    const text = `链接: ${share.getLink()}\n提取码: ${share.code || Lang.t('share.noPassword')}`;
    ClipboardUtil.copy(text, () => {
      MessageBoxUtil.success(Lang.t('share.copyLinkAndCodeSuccess'));
    });
  };

  // 渲染公开分享内容
  renderPublicShare() {
    const { share } = this.props;

    return (
      <>
        <Row className="info-row">
          <Col span={6} className="label">{Lang.t('share.link')}:</Col>
          <Col span={18} className="value">
            <Input.Group compact>
              <Input 
                style={{ width: 'calc(100% - 80px)' }} 
                value={share.getLink()} 
                readOnly 
              />
              <Button 
                type="primary" 
                icon={<CopyOutlined />}
                onClick={this.copyLink}
              >
                {Lang.t('copy')}
              </Button>
            </Input.Group>
          </Col>
        </Row>

        <Row className="info-row">
          <Col span={6} className="label">{Lang.t('share.code')}:</Col>
          <Col span={18} className="value">
            <Input.Group compact>
              <Input 
                style={{ width: 'calc(100% - 80px)' }} 
                value={share.code || Lang.t('share.noPassword')} 
                readOnly 
              />
              {share.code && (
                <Button 
                  type="primary" 
                  icon={<CopyOutlined />}
                  onClick={this.copyCode}
                >
                  {Lang.t('copy')}
                </Button>
              )}
            </Input.Group>
          </Col>
        </Row>

        <div className="action-section mt20">
          <div className="text-center">
            <Space>
              <Button 
                type="primary" 
                size="large"
                icon={<CopyOutlined />}
                onClick={this.copyBoth}
              >
                {Lang.t('share.copyLinkAndCode')}
              </Button>
              <Button 
                size="large"
                onClick={this.props.onClose}
              >
                {Lang.t('close')}
              </Button>
            </Space>
          </div>
        </div>
      </>
    );
  }

  // 渲染私有分享内容
  renderPrivateShare() {
    const targetUserUuids = this.getTargetUserUuids();
    const targetSpaceUuids = this.getTargetSpaceUuids();

    return (
      <>
        <Row className="info-row">
          <Col span={6} className="label">{Lang.t('share.targetUsers')}:</Col>
          <Col span={18} className="value">
            {targetUserUuids.length > 0 ? (
              <div className="target-list">
                {targetUserUuids.map((userUuid, index) => (
                  <Tag key={index} color="blue" icon={<UserOutlined />}>
                    {userUuid}
                  </Tag>
                ))}
              </div>
            ) : (
              <span className="no-target">{Lang.t('share.noTargetUsers')}</span>
            )}
          </Col>
        </Row>

        <Row className="info-row">
          <Col span={6} className="label">{Lang.t('share.targetSpaces')}:</Col>
          <Col span={18} className="value">
            {targetSpaceUuids.length > 0 ? (
              <div className="target-list">
                {targetSpaceUuids.map((spaceUuid, index) => (
                  <Tag key={index} color="green" icon={<TeamOutlined />}>
                    {spaceUuid}
                  </Tag>
                ))}
              </div>
            ) : (
              <span className="no-target">{Lang.t('share.noTargetSpaces')}</span>
            )}
          </Col>
        </Row>

        <div className="action-section mt20">
          <div className="text-center">
            <Button 
              size="large"
              onClick={this.props.onClose}
            >
              {Lang.t('close')}
            </Button>
          </div>
        </div>
      </>
    );
  }

  render() {
    const { share, onClose } = this.props;
    const isPrivateShare = share.isPrivateShare();

    return (
      <div className="widget-share-modal">
        <div className="share-info-section">
          <Row className="info-row">
            <Col span={6} className="label">{Lang.t('share.name')}:</Col>
            <Col span={18} className="value">{share.name}</Col>
          </Row>
          
          <Row className="info-row">
            <Col span={6} className="label">{Lang.t('share.shareScope')}:</Col>
            <Col span={18} className="value">
              <Tag color={isPrivateShare ? 'green' : 'blue'}>
                {share.getShareScopeText()}
              </Tag>
            </Col>
          </Row>

          <Row className="info-row">
            <Col span={6} className="label">{Lang.t('share.expireTime')}:</Col>
            <Col span={18} className="value">
              {share.expireInfinity 
                ? Lang.t('share.noExpire')
                : share.expireTime 
                  ? DateUtil.simpleDateHourMinute(share.expireTime)
                  : Lang.t('share.unknown')
              }
            </Col>
          </Row>

          {/* 根据分享类型显示不同内容 */}
          {isPrivateShare ? this.renderPrivateShare() : this.renderPublicShare()}
        </div>
      </div>
    );
  }
}